import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MergeIcon from "@mui/icons-material/Merge";
import Chip from "@mui/joy/Chip";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Person, PersonId } from "../../../models/person";
import { Link as BundleLink } from "../../../models/bundle";
import AlertDialogModal from "../../../shared/components/ui/AlertDialogModal";
import { unixToDayjs } from "../../../shared/utils/time";
import MergePatientsModal from "./MergePatientsModal";
import UnifiedDataTable, {
  DataTableColumn,
  DataTableAction,
  ServerPagination,
} from "../../../shared/components/ui/UnifiedDataTable";

interface ListOfPatientsProps {
  readonly persons: Person[];
  readonly deletePersons: (personIds: PersonId[]) => Promise<void>;
  readonly mergePersons: (sourcePersonId: PersonId, targetPersonIds: PersonId[]) => Promise<void>;
  // Server pagination props
  readonly totalItems?: number;
  readonly currentPageSize?: number;
  readonly links?: BundleLink[];
  readonly onPageChange?: (url: string) => void;
}

export function ListOfPatients({
  persons,
  deletePersons,
  mergePersons,
  totalItems,
  currentPageSize,
  links,
  onPageChange,
}: ListOfPatientsProps) {
  const navigate = useNavigate();
  const [selectedPatients, setSelectedPatients] = React.useState<PersonId[]>([]);
  const [openDeletePatientModal, setOpenDeletePatientModal] = React.useState(false);
  const [openMergePatientsModal, setOpenMergePatientsModal] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [merging, setMerging] = React.useState(false);

  async function handleDeletePatient() {
    function reset() {
      setSelectedPatients([]);
      setOpenDeletePatientModal(false);
      setDeleting(false);
    }
    if (selectedPatients.length > 0) {
      await deletePersons(selectedPatients);
      reset();
    } else {
      reset();
    }
  }

  async function handleMerge(sourcePersonId: PersonId) {
    setMerging(true);
    await mergePersons(sourcePersonId, selectedPatients);
    setSelectedPatients([]);
    setMerging(false);
    setOpenMergePatientsModal(false);
  }

  const columns: DataTableColumn<Person>[] = [
    {
      key: "patient",
      header: "Patient",
      width: "220px",
      render: (person) => {
        if (!person) {
          return null;
        }

        const hasValidName =
          (person.family_name && person.given_name) ||
          (person.given_name !== "" && person.family_name !== "");

        if (hasValidName) {
          return (
            <div>
              {person.given_name} {person.family_name}
            </div>
          );
        }

        return <>Unknown</>;
      },
    },
    {
      key: "birth_date",
      header: "Birth date",
      width: "110px",
      render: (person) =>
        person.birth_date ? unixToDayjs(person.birth_date)?.format("DD MMM YYYY") || "" : "",
    },
    {
      key: "identifiers",
      header: "Identifiers",
      minWidth: "190px",
      render: (person) => (
        <>
          {person.external_ids &&
            person.external_ids.length > 0 &&
            person.external_ids
              .filter((id) => {
                return id.value !== "" && id.system !== "ODH";
              })
              .sort((a, b) => {
                if (a.system && b.system) {
                  return a.system.localeCompare(b.system);
                } else if (a.system) {
                  return -1;
                } else if (b.system) {
                  return 1;
                }
                return 0;
              })
              .map((id) => (
                <Chip key={`${id.system}-${id.value}`} sx={{ mt: 0.5, mr: 0.5 }}>
                  {id.system}: {id.value}
                </Chip>
              ))}
        </>
      ),
    },
    {
      key: "vital_status",
      header: "Vital status",
      width: "140px",
      render: (person) => (person.deceased ? "Deceased" : "Alive or unknown"),
    },
    {
      key: "gdpr",
      header: "GDPR",
      width: "140px",
      render: (person) =>
        person.consent_withdrawal_date ? (
          <Chip color="warning">Opposition</Chip>
        ) : (
          <Chip color="neutral">No opposition</Chip>
        ),
    },
  ];

  const actions: DataTableAction<Person>[] = [
    {
      icon: <MergeIcon />,
      label: "Merge duplicated patients",
      color: "neutral",
      variant: "outlined",
      onClick: (selectedPersons: Person[]) => {
        setSelectedPatients(selectedPersons.map((p) => p.person_id));
        setOpenMergePatientsModal(true);
      },
      minSelection: 2,
    },
    {
      icon: <DeleteOutlineIcon />,
      label: "Delete patient(s)",
      color: "danger",
      variant: "outlined",
      onClick: (selectedPersons: Person[]) => {
        setSelectedPatients(selectedPersons.map((p) => p.person_id));
        setOpenDeletePatientModal(true);
      },
      minSelection: 1,
    },
  ];

  const emptyState = {
    title: "No patient found",
  };

  // Create server pagination config if props are provided
  const serverPagination: ServerPagination | undefined =
    totalItems && currentPageSize && links && onPageChange
      ? {
          totalItems,
          currentPageSize,
          links,
          onPageChange,
        }
      : undefined;

  if (persons.length === 0) return <div>No patient found</div>;

  return (
    <>
      <AlertDialogModal
        alertMessage="Are you sure you want to delete this patient?"
        open={openDeletePatientModal}
        setOpen={setOpenDeletePatientModal}
        handleDelete={handleDeletePatient}
        loading={deleting}
      />
      <MergePatientsModal
        openMergePatientsModal={openMergePatientsModal}
        setOpenMergePatientsModal={setOpenMergePatientsModal}
        selectedPatients={selectedPatients}
        persons={persons}
        handleMerge={handleMerge}
        merging={merging}
      />

      <UnifiedDataTable
        data={persons}
        columns={columns}
        getItemId={(person) => person.person_id}
        selectable={true}
        selectedItems={selectedPatients}
        onSelectionChange={(newSelected) => setSelectedPatients(newSelected as PersonId[])}
        actions={actions}
        onRowClick={(person) => navigate(`/patient/${person.person_id}`)}
        serverPagination={serverPagination}
        pageSize={serverPagination ? undefined : 50}
        paginationThreshold={serverPagination ? undefined : 50}
        ariaLabel="patients table"
        emptyState={emptyState}
      />
    </>
  );
}
